import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { PenLine, ShieldCheck, AlertTriangle, Clock, Download, Eraser, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { navy: '#0D1B2A', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', paper: '#F7F9FB' };

const SignatureCanvas = ({ onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const pos = (e) => {
    const c = ref.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: ((t ? t.clientX : e.clientX) - r.left) * (c.width / r.width),
             y: ((t ? t.clientY : e.clientY) - r.top) * (c.height / r.height) };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = ref.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    drawing.current = true;
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext('2d');
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    dirty.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (dirty.current) onChange(ref.current.toDataURL('image/png'));
  };

  useEffect(() => {
    const ctx = ref.current.getContext('2d');
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0D1B2A';
  }, []);

  const clear = useCallback(() => {
    const c = ref.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange('');
  }, [onChange]);

  return (
    <div>
      <canvas
        ref={ref} width={760} height={200} data-testid="signature-canvas"
        className="w-full rounded-md touch-none cursor-crosshair bg-white"
        style={{ border: `1px dashed ${C.slate}66`, height: 160 }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[11px]" style={{ color: C.slate }}>Draw your signature above — mouse, finger or stylus</span>
        <button onClick={clear} data-testid="signature-clear-btn"
          className="flex items-center gap-1 text-[11px] hover:underline" style={{ color: C.slate }}>
          <Eraser className="w-3 h-3" /> Clear
        </button>
      </div>
    </div>
  );
};

export default function SignPage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  const [typed, setTyped] = useState('');
  const [png, setPng] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/esign/open/${token}`)
      .then(r => setDoc(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'This signing link is not valid'));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(`${API}/esign/sign/${token}`,
        { typed_name: typed, signature_png: png, consent });
      setDone(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'The signature could not be recorded');
    }
    setBusy(false);
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen px-4 py-10 md:py-16" style={{ background: C.paper }}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <span className="text-lg font-semibold tracking-tight" style={{ color: C.navy }}>
            under<span style={{ color: C.teal }}>AI</span>ter
          </span>
          <span className="text-[11px] uppercase tracking-[0.18em] mt-1" style={{ color: C.slate }}>Document signing</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (error && !doc) return (
    <Shell>
      <div className="bg-white rounded-lg p-8" style={{ border: `1px solid ${C.red}33`, borderLeft: `4px solid ${C.red}` }} data-testid="sign-error">
        <AlertTriangle className="w-6 h-6 mb-3" style={{ color: C.red }} />
        <h1 className="text-xl font-medium" style={{ color: C.navy }}>This link cannot be opened</h1>
        <p className="text-sm mt-2" style={{ color: C.slate }}>{error}</p>
        <p className="text-sm mt-4" style={{ color: C.slate }}>Links expire, and they can be withdrawn by whoever sent them. Reply to the email you received and ask for a fresh one.</p>
      </div>
    </Shell>
  );

  if (!doc) return <Shell><div className="bg-white rounded-lg p-8 animate-pulse h-48" /></Shell>;

  if (done || doc.status === 'signed') return (
    <Shell>
      <div className="bg-white rounded-lg p-8" style={{ border: `1px solid ${C.green}33`, borderLeft: `4px solid ${C.green}` }} data-testid="sign-complete">
        <ShieldCheck className="w-7 h-7 mb-3" style={{ color: C.green }} />
        <h1 className="text-2xl font-medium tracking-tight" style={{ color: C.navy }}>Signed — thank you</h1>
        <p className="text-sm mt-2" style={{ color: C.slate }}>
          {doc.title} has been signed and filed against your record. A certificate page recording who signed,
          when, and a fingerprint of the exact document is attached to the copy below.
        </p>
        {(done?.sha256_signed) && (
          <p className="text-[11px] font-mono mt-4 break-all" style={{ color: C.slate }} data-testid="sign-fingerprint">
            Document fingerprint (SHA-256): {done.sha256_signed}
          </p>
        )}
        <a href={`${API}/esign/preview/${token}`} target="_blank" rel="noreferrer" data-testid="sign-download-copy"
          className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-md text-sm font-medium text-white"
          style={{ background: C.navy }}>
          <Download className="w-4 h-4" /> Download your signed copy
        </a>
      </div>
    </Shell>
  );

  if (doc.status === 'expired') return (
    <Shell>
      <div className="bg-white rounded-lg p-8" style={{ border: `1px solid ${C.amber}44`, borderLeft: `4px solid ${C.amber}` }} data-testid="sign-expired">
        <Clock className="w-6 h-6 mb-3" style={{ color: C.amber }} />
        <h1 className="text-xl font-medium" style={{ color: C.navy }}>This link has expired</h1>
        <p className="text-sm mt-2" style={{ color: C.slate }}>Ask {doc.sender || 'your insurance contact'} to send a fresh signing link.</p>
      </div>
    </Shell>
  );

  const ready = typed.trim().length > 2 && png && consent && !busy;

  return (
    <Shell>
      <div className="space-y-5" data-testid="sign-page">
        <div className="bg-white rounded-lg p-6 md:p-8" style={{ border: '1px solid rgba(136,150,168,0.18)' }}>
          <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: C.teal }}>{doc.kind_label}</p>
          <h1 className="text-2xl md:text-3xl font-medium tracking-tight mt-1" style={{ color: C.navy }} data-testid="sign-title">{doc.title}</h1>
          <p className="text-sm mt-2" style={{ color: C.slate }}>
            {doc.sender} has asked you to sign this document{doc.business_name ? ` on behalf of ${doc.business_name}` : ''}.
            {doc.message ? ` “${doc.message}”` : ''}
          </p>
          <p className="text-[11px] mt-3" style={{ color: C.slate }}>
            Open by {new Date(doc.expires_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.18)' }}>
          <div className="flex items-center gap-2 px-5 py-3" style={{ borderBottom: '1px solid rgba(136,150,168,0.14)' }}>
            <FileText className="w-4 h-4" style={{ color: C.slate }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>The document</span>
            <a href={`${API}/esign/preview/${token}`} target="_blank" rel="noreferrer"
              className="ml-auto text-[11px] hover:underline" style={{ color: C.teal }} data-testid="sign-open-pdf">Open in a new tab</a>
          </div>
          <iframe src={`${API}/esign/preview/${token}`} title="Document" data-testid="sign-pdf-frame"
            className="w-full" style={{ height: 460, border: 0, background: '#EEF1F5' }} />
        </div>

        <div className="bg-white rounded-lg p-6 md:p-8 space-y-5" style={{ border: '1px solid rgba(136,150,168,0.18)' }}>
          <div className="flex items-start gap-2">
            <PenLine className="w-4 h-4 mt-0.5" style={{ color: C.teal }} />
            <p className="text-sm font-medium" style={{ color: C.navy }}>{doc.statement}</p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: C.navy }}>
              Your full name
            </label>
            <input
              value={typed} onChange={e => setTyped(e.target.value)} data-testid="sign-typed-name"
              placeholder={doc.signer_name}
              className="w-full px-3 py-2.5 rounded-md text-sm outline-none focus:ring-2"
              style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy }}
            />
            <p className="text-[11px] mt-1" style={{ color: C.slate }}>
              Must match the name on the document — <strong>{doc.signer_name}</strong>
            </p>
          </div>

          <SignatureCanvas onChange={setPng} />

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
              data-testid="sign-consent-checkbox" className="mt-0.5 w-4 h-4 shrink-0" />
            <span className="text-[11px] leading-relaxed" style={{ color: C.slate }}>{doc.consent_text}</span>
          </label>

          {error && (
            <p className="text-xs px-3 py-2 rounded-md" data-testid="sign-submit-error"
              style={{ color: C.red, background: 'rgba(226,75,74,0.07)', border: `1px solid ${C.red}33` }}>{error}</p>
          )}

          <button onClick={submit} disabled={!ready} data-testid="sign-submit-button"
            className="w-full md:w-auto px-6 py-3 rounded-md text-sm font-semibold text-white transition-opacity disabled:opacity-40"
            style={{ background: C.navy }}>
            {busy ? 'Recording your signature…' : 'Sign and return'}
          </button>
          <p className="text-[11px]" style={{ color: C.slate }}>
            Your name, the time, your network address and device are recorded with the signature as evidence,
            and a fingerprint of the signed document is sealed into an audit trail that cannot be edited.
          </p>
        </div>
      </div>
    </Shell>
  );
}
